import {Injectable} from "@angular/core";
import {AbstractControl, FormGroup, NgForm} from "@angular/forms";
import {ResponseDto} from "../models/exceptions/response-dto";
import {FormErrorDto, IFormExceptionDto} from "../models/exceptions/form-error-dto";
import {BodyErrorDto, IBodyExceptionDto} from "../models/exceptions/body-error-dto";
import {IRuleExceptionDto, RuleErrorDto} from "../models/exceptions/rule-error-dto";
import {SnackbarService} from "../shared/services/snackbar.service";
import {messages} from "./messages";
import { IUserPendingValidationsDto } from "@models/portal/validator/dto/user-pending-validation-dto";

@Injectable({
  providedIn: 'root'
})
export class MessageErrorResolver {

  private static readonly FORM_VALIDATION_GENERIC_MESSAGE =
    'Ocorreram erros de validação de formulário!';

  private formKeyParam: Map<string, string> = new Map<string, string>();
  private formKeyValidationArgument: Map<string, ParamValidationArgument[]> = new Map<string, ParamValidationArgument[]>();
  private formValidationMessages: Map<string, string> = new Map<string, string>();
  private formValidationMessagesBackup: Map<string, string> = new Map<string, string>();

  constructor(private snackbarService: SnackbarService) {
  }

  public clone(): MessageErrorResolver {
    return new MessageErrorResolver(this.snackbarService);
  }

  public setupParam(formKey: string, apiParam: string, args?: ParamValidationArgument[]): void {
    const keyJaExistente: string | undefined = this.formKeyParam.get(formKey);
    if (!keyJaExistente) {
      this.formKeyParam.set(formKey, apiParam);
      if (args) this.formKeyValidationArgument.set(formKey, args);
      this.formValidationMessages.set(formKey, "");
      this.formValidationMessagesBackup.set(formKey, "");
    }
  }

  public resolveMessageWithForm(responseDto: ResponseDto<any>, form: NgForm): Map<string, string> {
    return this.resolveMessageWithReactiveForm(responseDto, (apiLabel) =>
      this.findControlForRegisteredParam(form, apiLabel),
    );
  }

  public resolveMessageWithReactiveForm(
    responseDto: ResponseDto<any>,
    getControl: (apiLabel: string, errorMessage?: string) => AbstractControl | null,
  ): Map<string, string> {
    if (responseDto.type === 0) return new Map<string, string>;
    this.formValidationMessages = new Map<string, string>(this.formValidationMessagesBackup);

    if (responseDto.type === 1) {
      const formExceptionDto: FormErrorDto = (responseDto.response as IFormExceptionDto);
      this.formValidationMessages = this.resolveValidations(formExceptionDto, getControl);
    }

    if (responseDto.type === 2) {
      const bodyExceptionDto: BodyErrorDto = (responseDto.response as IBodyExceptionDto);
      this.snackbarService.showWarn(bodyExceptionDto.message);
    }

    if (responseDto.type === 3 || responseDto.type === 4) {
      const ruleExceptionDto: RuleErrorDto = (responseDto.response as IRuleExceptionDto);
      this.snackbarService.showWarn(ruleExceptionDto.message);
    }

    if (responseDto.type === 5) {
      const bodyExceptionDto: IUserPendingValidationsDto = (responseDto.response);
      this.resolveObjectError(bodyExceptionDto);
    }

    return this.formValidationMessages;
  }

  public resolveMessage(responseDto: ResponseDto<any>,showSnackbar: boolean = true): Map<string, string> {
    if (responseDto.type === 0) return new Map<string, string>;
    this.formValidationMessages = new Map<string, string>(this.formValidationMessagesBackup);

    if (responseDto.type === 1) {
      this.snackbarService.showWarn(this.getFormValidationSnackbarMessage(responseDto));
    }

    if (responseDto.type === 2 && showSnackbar) {
      const bodyExceptionDto: BodyErrorDto = (responseDto.response as IBodyExceptionDto);
      this.snackbarService.showWarn(bodyExceptionDto.message);
    }

    if (responseDto.type === 3 && showSnackbar) {
      const ruleExceptionDto: RuleErrorDto = (responseDto.response as IRuleExceptionDto);
      this.snackbarService.showWarn(ruleExceptionDto.message);
    }

    if (responseDto.type === 5 && showSnackbar) {
      const bodyExceptionDto: IUserPendingValidationsDto = (responseDto.response);
      this.resolveObjectError(bodyExceptionDto);
    }

    return this.formValidationMessages;
  }

  resolveObjectError(bodyExceptionDto: IUserPendingValidationsDto) {
    const { uvcListCampos, uvcListContatos, uvcListAnexos, uvcListDocumentos, uvcListLogradouro, uvcListDadosBancarios } = bodyExceptionDto;

    let message = `
      As alterações no cadastro do usuário ${bodyExceptionDto.usuTxNome} conflitam com os requisitos do evento em que ele está inscrito.<br>
      <strong>Por favor, revise e preencha os seguintes campos obrigatórios:</strong><br>
    `;

    const sections = [];

    if (uvcListCampos?.length! > 0) {
      sections.push(`<strong>Dados Básicos:</strong> ${uvcListCampos?.join(', ')}`);
    }

    if (uvcListContatos?.length! > 0) {
      sections.push(`<strong>Contatos:</strong> ${uvcListContatos?.join(', ')}`);
    }

    if (uvcListAnexos?.length! > 0) {
      sections.push(`<strong>Anexos:</strong> ${uvcListAnexos?.join(', ')}`);
    }

    if (uvcListDocumentos?.length! > 0) {
      sections.push(`<strong>Documentos:</strong> ${uvcListDocumentos?.join(', ')}`);
    }

    if (uvcListDadosBancarios?.length! > 0) {
      sections.push(`<strong>Dados Bancarios:</strong> ${uvcListDadosBancarios?.join(', ')}`);
    }

    if (uvcListLogradouro?.length! > 0) {
      sections.push(`<strong>Logradouro:</strong> ${uvcListLogradouro?.join(', ')}`);
    }

    if (sections.length > 0) {
      message += sections.join('<br>') + '<br>Esses campos são necessários para garantir sua participação no evento.';
    } else {
      message += 'Nenhum campo adicional é necessário.';
    }

    this.snackbarService.showWarn(message, true); // Passa o `isHtml` como true
  }



  public getFieldApiMessage(messageKey: string): string {
    return this.formValidationMessages.get(messageKey) ?? '';
  }

  public setFieldApiMessage(messageKey: string, message: string): void {
    this.formValidationMessages.set(messageKey, message);
  }

  public showErrorMessageForKey(
    form: NgForm | FormGroup,
    key: string,
    messageKey?: string,
  ): string {
    let message: string | undefined;
    const validationMessageKey = messageKey ?? key;
    const control = form.controls[key];
    const apiMessage = this.formValidationMessages.get(validationMessageKey);

    if (apiMessage && (control?.errors?.['apiError'] || control?.invalid)) {
      return apiMessage;
    }

    if ((control?.invalid && control?.touched) || control?.dirty) {

      if (control.errors?.['apiError'] && apiMessage) {
        return apiMessage;
      }

      const validationTypesKeys: string[] = Object.keys(ValidationTypes);
      validationTypesKeys.forEach((validationKey: string) => {

        if (control.errors?.[validationKey]) {

          if (validationKey === 'apiError') {
            message = apiMessage;
            return;
          }

          message = messages.validation[validationKey];
          message = this.setValidationArgumentForMessage(key, validationKey, message!);
          return;

        }

      });

    }

    return message ? message : "";
  }

  private setValidationArgumentForMessage(key: string, validationKey: string, currentMessage: string): string {
    const keyArguments = this.formKeyValidationArgument.get(key);

    if (keyArguments) {

      const paramValidationArgument = keyArguments.filter(argument =>
        argument.validation === ValidationTypes[validationKey as keyof typeof ValidationTypes])[0];

      if (paramValidationArgument) {
        currentMessage = currentMessage.slice(0, -1);
        currentMessage = currentMessage?.concat(": ", paramValidationArgument.value, ".");
      }

    }

    return currentMessage;
  }

  private resolveValidations(
    formExceptionDto: IFormExceptionDto,
    getControl: (apiLabel: string, errorMessage?: string) => AbstractControl | null,
  ): Map<string, string> {
    const formValidationMessages: Map<string, string> = new Map<string, string>();

    const registeredLabels = new Set([
      ...this.formKeyParam.keys(),
      ...this.formKeyParam.values(),
    ]);
    const fieldMessagesNotRegistered = formExceptionDto.errors.filter(
      (error) => !registeredLabels.has(error.label),
    );
    fieldMessagesNotRegistered.forEach(fieldMessage => {
      const randomKey = Math.floor(Math.random() * 1000000000);
      this.setupParam("#".concat(randomKey.toString()), fieldMessage.label);
    });

    this.formKeyParam.forEach((value, key) => {

      const matchingErrors = formExceptionDto.errors.filter(
        (error) => error.label === value,
      );
      const messagesOfKey: string = matchingErrors
        .map((error) => error.message)
        .join("; ");

      if (messagesOfKey) {
        const messagesOfKeyTransformed: string = messagesOfKey.charAt(0).toUpperCase() + messagesOfKey.slice(1);

        if (key.includes("#")) {
          this.snackbarService.showWarn(messagesOfKeyTransformed);
        }

        else {
          const control = getControl(value, matchingErrors[0]?.message);
          if (control) {
            control.setErrors({ ...(control.errors ?? {}), apiError: true });
            control.markAsTouched();
            formValidationMessages.set(key, messagesOfKeyTransformed);
          } else {
            this.snackbarService.showWarn(messagesOfKeyTransformed);
          }
        }

      }
    });
    return formValidationMessages;
  }

  private findControlForRegisteredParam(
    form: NgForm | FormGroup,
    apiLabel: string,
  ): AbstractControl | null {
    for (const [formKey, param] of this.formKeyParam) {
      if (param === apiLabel) {
        const control = form.controls[formKey];
        if (control) return control;
      }
    }
    return form.controls[apiLabel] ?? null;
  }

  private getFormValidationSnackbarMessage(responseDto: ResponseDto<any>): string {
    const formException = responseDto.response as IFormExceptionDto | undefined;
    const firstError = formException?.errors?.[0];
    const apiMessage = firstError?.message?.trim();

    if (!apiMessage) {
      return MessageErrorResolver.FORM_VALIDATION_GENERIC_MESSAGE;
    }

    return apiMessage.charAt(0).toUpperCase() + apiMessage.slice(1);
  }
}

export interface ParamValidationArgument {
  validation: ValidationTypes,
  value: any
}

export enum ValidationTypes {
  required,
  minlength,
  maxlength,
  apiError,
  birthdate,
  mask,
  min,
  max,
  endYearMax,
  endYearMin,
  email,
  pattern,
  startDateInvalid,
  endDateInvalid,
  startDateSubscriptionInvalid,
  endDateSubscriptionInvalid,
  endDateSubscriptionAfterBlock,
  blockDateInvalid,
  blockEndDateInvalid,
  blockDateLimitAttachemnt1,
  blockDateLimitAttachemnt2,
  competitionDateInvalid
}
